import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckItem, NonMedicalNote } from '../components/ui'
import { PHONE_DISPLAY, PHONE_TEL } from '../lib/constants'
import { submitInquiry } from '../lib/api'

const TIME_BLOCKS = [
  'Morning (9:00 AM – 12:00 PM)',
  'Early afternoon (12:00 PM – 3:00 PM)',
  'Late afternoon (3:00 PM – 5:30 PM)',
  'Evening (5:30 PM – 7:30 PM)',
]

const STEPS = [
  { t: 'Who needs care?', s: 'Choose the option that fits best.', kind: 'single', key: 'who', cols: '1fr 1fr', opts: ['My parent', 'My spouse or partner', 'Myself', 'Another family member or friend'] },
  { t: 'Where is home?', s: 'We use the postal code to confirm your service area.', kind: 'postal' },
  { t: 'What kind of support would help?', s: 'Choose as many as apply — you can adjust later.', kind: 'multi', cols: '1fr', opts: ['Personal care (bathing, dressing, grooming)', 'Meal preparation', 'Light housekeeping and laundry', 'Companionship and outings', 'Post-hospital recovery support', "Dementia or Alzheimer's support", 'Respite for a family caregiver', 'Overnight or extended support'] },
  { t: 'How often would you like support?', s: 'A rough idea is fine.', kind: 'single', key: 'frequency', cols: '1fr', opts: ['A few hours a week', 'A few times a week', 'Daily visits', 'Overnight or extended shifts', "Not sure yet — I'd like guidance"] },
  { t: 'Where would you like to meet?', s: 'The assessment is free and no-obligation.', kind: 'single', key: 'assessmentType', cols: '1fr', opts: ['In-home visit (most families choose this)', 'At our Brisebois Drive office', 'By phone'] },
  { t: 'Choose up to three times', s: 'Share dates and times that suit your family — we confirm by phone or email.', kind: 'slots' },
  { t: 'How can we reach you?', s: 'A care coordinator replies within one business day.', kind: 'contact' },
  { t: 'Review and send', s: 'Confirm the details below, then send your request.', kind: 'review' },
]

const NEXT_STEPS = [
  'A care coordinator reviews your request and confirms we serve your area.',
  'We call or email you within one business day to confirm a time.',
  'We meet, listen, and build your 5-Pillar Care Plan together.',
  'We match a screened caregiver and set up your Family Room portal access.',
]

function OptionButton({ label, selected, single, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        minHeight: 52,
        alignItems: 'center',
        gap: 12,
        borderRadius: 4,
        border: selected ? '1px solid var(--color-teal-800)' : '1px solid var(--color-line)',
        background: selected ? 'var(--color-teal-800)' : 'var(--color-panel)',
        color: selected ? 'var(--color-cream)' : 'var(--color-ink)',
        padding: '12px 16px',
        fontSize: 17,
        fontWeight: 600,
        fontFamily: 'var(--font-sans)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span
        style={{
          display: 'flex',
          height: 24,
          width: 24,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: single ? '50%' : 4,
          border: `2px solid ${selected ? 'var(--color-cream)' : 'var(--color-teal-800)'}`,
          background: selected ? 'transparent' : '#FFFFFF',
          color: 'var(--color-cream)',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {selected ? '✓' : ''}
      </span>
      {label}
    </button>
  )
}

export default function ScheduleAssessment() {
  const [step, setStep] = useState(0)
  const [done, setDone] = useState(false)
  const [refId, setRefId] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [support, setSupport] = useState([])
  const [slots, setSlots] = useState([
    { date: '', time: TIME_BLOCKS[0] },
    { date: '', time: TIME_BLOCKS[0] },
    { date: '', time: TIME_BLOCKS[0] },
  ])
  const [f, setFState] = useState({
    who: '', postal: '', frequency: '', assessmentType: '',
    contactName: '', contactEmail: '', contactPhone: '', preferredContact: 'Phone call', message: '', consent: false,
  })

  const cur = STEPS[step]
  const setF = (patch) => { setFState((prev) => ({ ...prev, ...patch })); setError('') }
  const onField = (e) => setF({ [e.target.name]: e.target.value })
  const setSlot = (i, patch) => { setSlots((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s))); setError('') }

  const validate = () => {
    if (cur.kind === 'single' && !f[cur.key]) {
      return cur.key === 'who' ? 'Please choose who needs care.'
        : cur.key === 'frequency' ? 'Please choose roughly how often support would help.'
        : "Please choose where you'd like to meet."
    }
    if (cur.kind === 'postal' && f.postal.trim().length < 3) return 'Please enter your postal code.'
    if (cur.kind === 'multi' && support.length === 0) return 'Please choose at least one kind of support.'
    if (cur.kind === 'slots' && !slots.some((s) => s.date)) return 'Please add at least one preferred date.'
    if (cur.kind === 'contact') {
      if (f.contactName.trim().length < 2) return 'Please tell us your name.'
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contactEmail.trim())) return 'Please enter an email address we can reply to.'
    }
    if (cur.kind === 'review' && !f.consent) return 'Please check the consent box so we can contact you about this request.'
    return ''
  }

  const submit = async () => {
    const [firstName, ...rest] = f.contactName.trim().split(' ')
    setBusy(true)
    try {
      const { reference } = await submitInquiry({
        kind: 'care',
        firstName,
        lastName: rest.join(' '),
        email: f.contactEmail,
        phone: f.contactPhone,
        postalCode: f.postal,
        whoNeedsCare: f.who,
        relationship: '',
        services: support,
        frequency: f.frequency,
        assessmentType: f.assessmentType,
        preferredSlots: slots.filter((s) => s.date).map((s) => ({ date: s.date, timeBlock: s.time })),
        preferredContact: f.preferredContact,
        message: '[Prefers ' + f.preferredContact + '] ' + f.message,
        consent: true,
      })
      setRefId(reference)
      setDone(true)
      setError('')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const next = async () => {
    if (busy) return
    const err = validate()
    if (err) { setError(err); return }
    if (cur.kind === 'review') await submit()
    else { setStep(step + 1); setError('') }
  }

  const back = () => { setStep(Math.max(0, step - 1)); setError('') }

  const fmtSlot = (s) => (s.date ? s.date + ' · ' + s.time : null)
  const review = [
    { k: 'Who needs care', v: f.who },
    { k: 'Postal code', v: f.postal },
    { k: 'Support', v: support.join(', ') },
    { k: 'Frequency', v: f.frequency },
    { k: 'Meeting', v: f.assessmentType },
    { k: 'Preferred times', v: slots.map(fmtSlot).filter(Boolean).join(' · ') || '—' },
    { k: 'Contact', v: f.contactName + ' · ' + f.contactEmail + (f.contactPhone ? ' · ' + f.contactPhone : '') + ' · prefers ' + f.preferredContact },
  ]

  if (done) {
    return (
      <section style={{ background: 'var(--color-panel)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '96px 24px' }}>
          <p className="eyebrow">Request received</p>
          <h1 className="h1-hero" style={{ margin: '16px 0 0 0', maxWidth: 768 }}>
            Thank you — your assessment request is with our care team.
          </h1>
          <p style={{ margin: '24px 0 0 0', maxWidth: 672, fontSize: 19, lineHeight: 1.7, color: 'var(--color-muted)' }}>
            Your reference number is <strong style={{ color: 'var(--color-teal-900)' }}>{refId}</strong>. Keep it handy if you call us.
          </p>
          <div style={{ marginTop: 40, maxWidth: 672, borderRadius: 8, border: '1px solid var(--color-line)', background: '#FFFFFF', padding: 32 }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem', lineHeight: 1.2, color: 'var(--color-teal-900)' }}>What happens next</h2>
            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {NEXT_STEPS.map((n) => <CheckItem key={n}>{n}</CheckItem>)}
            </div>
            <div style={{ marginTop: 32, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <a href={PHONE_TEL} className="btn btn-primary">Call {PHONE_DISPLAY}</a>
              <Link to="/how-it-works" className="btn btn-secondary">See how care begins</Link>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <>
      <section style={{ background: 'var(--color-panel)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '64px 24px' }}>
          <p className="eyebrow">Schedule an assessment</p>
          <h1 className="h1-hero" style={{ margin: '16px 0 0 0', maxWidth: 768 }}>
            A few gentle questions, then we'll find a time to talk.
          </h1>
          <p style={{ margin: '24px 0 0 0', maxWidth: 672, fontSize: 19, lineHeight: 1.7, color: 'var(--color-muted)' }}>
            Nothing here is a commitment. If you'd rather answer these by phone, call {PHONE_DISPLAY} and we'll fill it in with you.
          </p>
        </div>
      </section>

      <section style={{ padding: '80px 0', background: 'var(--color-cream)' }}>
        <div style={{ maxWidth: 768, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 17, fontWeight: 600, color: 'var(--color-teal-900)' }}>
            <span>Step {step + 1} of 8</span>
            <span>{Math.round(((step + 1) / 8) * 100)}% complete</span>
          </div>
          <div style={{ marginTop: 12, height: 12, width: '100%', overflow: 'hidden', borderRadius: 9999, background: 'var(--color-panel-deep)' }}>
            <div style={{ height: '100%', borderRadius: 9999, background: 'var(--color-sage)', width: `${Math.round(((step + 1) / 8) * 100)}%`, transition: 'width 0.3s' }} />
          </div>

          <div style={{ marginTop: 32, borderRadius: 8, border: '1px solid var(--color-line)', background: '#FFFFFF', padding: 'clamp(20px, 5vw, 40px)' }}>
            <h2 style={{ margin: '0 0 6px 0', fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', lineHeight: 1.15, color: 'var(--color-teal-900)' }}>{cur.t}</h2>
            <p style={{ margin: '0 0 28px 0', fontSize: 17, color: 'var(--color-muted)' }}>{cur.s}</p>

            {(cur.kind === 'single' || cur.kind === 'multi') && (
              <div className="grid" style={{ '--cols': cur.cols || 'minmax(0, 1fr)', gap: 12 }}>
                {cur.opts.map((label) => {
                  const sel = cur.kind === 'single' ? f[cur.key] === label : support.includes(label)
                  return (
                    <OptionButton
                      key={label}
                      label={label}
                      selected={sel}
                      single={cur.kind === 'single'}
                      onClick={() => {
                        if (cur.kind === 'single') setF({ [cur.key]: label })
                        else { setSupport(sel ? support.filter((x) => x !== label) : [...support, label]); setError('') }
                      }}
                    />
                  )
                })}
              </div>
            )}

            {cur.kind === 'postal' && (
              <>
                <div style={{ maxWidth: 320, width: '100%' }}>
                  <label className="field-label" htmlFor="postal">Postal code</label>
                  <input id="postal" name="postal" className="field-input" placeholder="e.g. T2L 2G3" value={f.postal} onChange={onField} />
                </div>
                <p style={{ margin: '16px 0 0 0', fontSize: 16, color: 'var(--color-muted)' }}>We serve Calgary and surrounding communities.</p>
              </>
            )}

            {cur.kind === 'slots' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {slots.map((s, i) => (
                  <div key={i} className="grid" style={{ '--cols': '1fr 1.4fr', gap: 16 }}>
                    <div>
                      <label className="field-label" htmlFor={`slot-date-${i}`}>{'Preferred date ' + (i + 1) + (i === 0 ? '' : ' (optional)')}</label>
                      <input id={`slot-date-${i}`} type="date" className="field-input" value={s.date} onChange={(e) => setSlot(i, { date: e.target.value })} />
                    </div>
                    <div>
                      <label className="field-label" htmlFor={`slot-time-${i}`}>Time of day</label>
                      <select id={`slot-time-${i}`} className="field-input" value={s.time} onChange={(e) => setSlot(i, { time: e.target.value })}>
                        {TIME_BLOCKS.map((t) => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {cur.kind === 'contact' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div>
                  <label className="field-label" htmlFor="contactName">Your name *</label>
                  <input id="contactName" name="contactName" className="field-input" value={f.contactName} onChange={onField} />
                </div>
                <div className="grid grid-2" style={{ gap: 24 }}>
                  <div>
                    <label className="field-label" htmlFor="contactEmail">Email *</label>
                    <input id="contactEmail" name="contactEmail" type="email" className="field-input" value={f.contactEmail} onChange={onField} />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="contactPhone">Phone</label>
                    <input id="contactPhone" name="contactPhone" type="tel" className="field-input" value={f.contactPhone} onChange={onField} />
                  </div>
                </div>
                <div>
                  <label className="field-label" htmlFor="preferredContact">Preferred contact method</label>
                  <select id="preferredContact" name="preferredContact" className="field-input" value={f.preferredContact} onChange={onField}>
                    <option>Phone call</option>
                    <option>Email</option>
                    <option>Text message</option>
                  </select>
                </div>
                <div>
                  <label className="field-label" htmlFor="message">Anything else we should know?</label>
                  <textarea id="message" name="message" className="field-input" style={{ minHeight: 110 }} value={f.message} onChange={onField} />
                </div>
              </div>
            )}

            {cur.kind === 'review' && (
              <>
                <div style={{ borderRadius: 8, border: '1px solid var(--color-line)', background: 'var(--color-panel)', padding: 'clamp(16px, 4vw, 28px)', display: 'flex', flexDirection: 'column', gap: 10, fontSize: 17 }}>
                  {review.map((r) => (
                    <div key={r.k} style={{ overflowWrap: 'anywhere' }}>
                      <strong style={{ color: 'var(--color-teal-900)' }}>{r.k}:</strong>{' '}
                      <span style={{ color: 'var(--color-muted)' }}>{r.v}</span>
                    </div>
                  ))}
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 16, color: 'var(--color-muted)', cursor: 'pointer', marginTop: 24 }}>
                  <input
                    type="checkbox"
                    checked={f.consent}
                    onChange={(e) => setF({ consent: e.target.checked })}
                    style={{ width: 24, height: 24, minHeight: 24, marginTop: 2 }}
                  />
                  I agree that True North Care &amp; Support may contact me about this care request.
                </label>
              </>
            )}

            {error && (
              <p
                className="field-error"
                style={{ margin: '24px 0 0 0', borderRadius: 4, border: '1px solid oklch(0.514 0.168 27.5 / 40%)', background: 'oklch(0.514 0.168 27.5 / 8%)', padding: '12px 16px', fontSize: 16, fontWeight: 600 }}
              >
                {error}
              </p>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 16, marginTop: 32 }}>
              {step > 0 && (
                <button type="button" onClick={back} className="btn btn-secondary" style={{ flex: '0 1 auto' }}>
                  ← Back
                </button>
              )}
              <button type="button" onClick={next} disabled={busy} className="btn btn-primary" style={{ flex: '0 1 auto', marginLeft: 'auto' }}>
                {busy ? 'Sending…' : cur.kind === 'review' ? 'Send assessment request' : 'Continue →'}
              </button>
            </div>
          </div>

          <div style={{ marginTop: 24 }}>
            <NonMedicalNote />
          </div>
        </div>
      </section>
    </>
  )
}
